/**
 * useTaskActions Hook
 * 
 * Composes all task CRUD operations from focused sub-hooks.
 * Now with undo/redo support via recordAction.
 * 
 * @param {Object} localSprint - Current sprint state
 * @param {Function} setLocalSprint - State setter for local sprint
 * @param {Function} recordAction - Function to record undoable actions (optional)
 * @param {Object} options - Additional options for task operations
 * @param {Array} options.nestedSprints - Array of nested sprints (for name sync)
 * @param {Function} options.onUpdateSprint - Callback to update a sprint
 * @param {Function} options.onDateValidationError - Callback when date validation fails
 */

import { useTaskCrud } from './useTaskCrud';
import { useTaskStatus } from './useTaskStatus';
import { useTaskEdit } from './useTaskEdit';
import { useTaskContent } from './useTaskContent';
import { useDecisionNode } from './useDecisionNode';
import { useTaskArchive } from './useTaskArchive';

export const useTaskActions = (localSprint, setLocalSprint, recordAction = null, options = {}) => {
  // Compose all task action hooks
  const crud = useTaskCrud(localSprint, setLocalSprint, recordAction);
  const status = useTaskStatus(localSprint, setLocalSprint, recordAction);
  const edit = useTaskEdit(localSprint, setLocalSprint, recordAction, options);
  const content = useTaskContent(localSprint, setLocalSprint, recordAction, options.onDateValidationError);
  const decision = useDecisionNode(localSprint, setLocalSprint, recordAction);
  const archive = useTaskArchive(localSprint, setLocalSprint, recordAction);

  return {
    // CRUD operations
    handleAddTask: crud.handleAddTask,
    handleCreateConnectedTask: crud.handleCreateConnectedTask,
    handleInsertNodeOnEdge: crud.handleInsertNodeOnEdge,
    handleDeleteTask: crud.handleDeleteTask,
    handleDeleteTasks: crud.handleDeleteTasks,
    handleDuplicateTask: crud.handleDuplicateTask,
    
    // Status operations
    handleTaskStatusChange: status.handleTaskStatusChange,
    handleDependencyChange: status.handleDependencyChange,
    handleConditionsChange: status.handleConditionsChange,
    
    // Edit operations
    handleEditTask: edit.handleEditTask,
    handleRenameTask: edit.handleRenameTask,
    handleTaskResize: edit.handleTaskResize,
    handleTaskResizeEnd: edit.handleTaskResizeEnd,
    handleIconChange: edit.handleIconChange,
    clearDeletedIconFromAllTasks: edit.clearDeletedIconFromAllTasks,
    handleSetStatusAutoSync: edit.handleSetStatusAutoSync,
    handleMilestoneAnchorChange: edit.handleMilestoneAnchorChange,
    
    // Content operations
    handleNotesChange: content.handleNotesChange,
    handleInternalNotesChange: content.handleInternalNotesChange,
    handleMissionDocChange: content.handleMissionDocChange,
    handleMissionDocTicksChange: content.handleMissionDocTicksChange,
    handleMissionDocFilesChange: content.handleMissionDocFilesChange,
    handleMissionVariablesChange: content.handleMissionVariablesChange,
    handleAttachedDocsChange: content.handleAttachedDocsChange,
    handleQuickNotesChange: content.handleQuickNotesChange,
    handleMissionDocsMigrated: content.handleMissionDocsMigrated,
    handleClearMissionLegacyNotes: content.handleClearMissionLegacyNotes,
    handleDeliverablesChange: content.handleDeliverablesChange,
    handleInputsChange: content.handleInputsChange,
    handleDueDateChange: content.handleDueDateChange,
    handleEstimatedDurationMinutesChange: content.handleEstimatedDurationMinutesChange,
    handleCreatedAtChange: content.handleCreatedAtChange,
    handleStartedAtChange: content.handleStartedAtChange,
    handleCompletedAtChange: content.handleCompletedAtChange,
    handleUpdatedAtChange: content.handleUpdatedAtChange,
    handlePriorityChange: content.handlePriorityChange,
    handleDifficultyChange: content.handleDifficultyChange,
    handleTaskStartDateChange: content.handleTaskStartDateChange,
    handleTaskEndDateChange: content.handleTaskEndDateChange,
    handleTaskDateRangeChange: content.handleTaskDateRangeChange,
    handleRemindersChange: content.handleRemindersChange,
    handleAttachmentCountChange: content.handleAttachmentCountChange,
    handleCustomFieldValueChange: content.handleCustomFieldValueChange,
    
    // Decision node operations
    handleDecisionAnswerChange: decision.handleDecisionAnswerChange,
    handleDecisionOptionsChange: decision.handleDecisionOptionsChange,
    
    // Archive operations
    handleArchiveTask: archive.handleArchiveTask,
    handleArchiveTasks: archive.handleArchiveTasks,
    handleUnarchiveTask: archive.handleUnarchiveTask,
    handleAutoArchiveCompletedTasks: archive.handleAutoArchiveCompletedTasks,
  };
};

export default useTaskActions;

